import { useState, useEffect, useRef } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import WeatherModal from "./WeatherModal";

export default function WeatherStrip({ segments, tripId }) {
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const stripRef = useRef(null);

  // Publish the strip's height so scroll offsets can clear header + trip header + strip.
  useEffect(() => {
    const root = document.documentElement;
    const el = stripRef.current;
    if (!el) { root.style.setProperty("--weather-strip-height", "0px"); return; }
    const update = () => root.style.setProperty("--weather-strip-height", `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { ro.disconnect(); root.style.setProperty("--weather-strip-height", "0px"); };
  }, [cities.length]);

  useEffect(() => {
    if (!segments || segments.length === 0) return;

    const sixMonthsAgo = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000)
      .toISOString().split("T")[0];

    // Hotel segments from last 6 months onward
    const hotelSegs = segments
      .filter(s => s.type === "hotel" && s.date && s.date >= sixMonthsAgo)
      .sort((a, b) => a.date.localeCompare(b.date));

    // Fall back to flight segments if no hotels
    const flightSegs = segments
      .filter(s => s.type === "flight" && s.date && s.date >= sixMonthsAgo)
      .sort((a, b) => a.date.localeCompare(b.date));

    const candidates = hotelSegs.length > 0 ? hotelSegs : flightSegs;

    const resolve = async () => {
      const seen = new Set();
      const result = [];

      for (const seg of candidates) {
        let city = seg.destinationCity || null;
        let countryCode = seg.destinationCountryCode || null;
        let lat = typeof seg.destinationLat === "number" ? seg.destinationLat : null;
        let lng = typeof seg.destinationLng === "number" ? seg.destinationLng : null;

        // Geocode when the city is missing OR we have no coordinates yet.
        // The result is cached back to Firestore so each segment is only geocoded once.
        const needsGeocode = seg.address && (!city || lat == null || lng == null);
        if (needsGeocode) {
          try {
            const resp = await fetch(`/api/places?address=${encodeURIComponent(seg.address)}`);
            const data = await resp.json();
            if (data.city) {
              city = data.city;
              countryCode = data.countryCode || countryCode;
              if (typeof data.lat === "number" && typeof data.lng === "number") {
                lat = data.lat;
                lng = data.lng;
              }
              // Write back to Firestore so we never geocode this segment again
              if (tripId && seg.id) {
                updateDoc(doc(db, "trips", tripId, "segments", seg.id), {
                  destinationCity: city,
                  ...(countryCode ? { destinationCountryCode: countryCode } : {}),
                  ...(lat != null && lng != null
                    ? { destinationLat: lat, destinationLng: lng }
                    : {}),
                }).catch(() => {});
              }
            }
          } catch {
            // silently skip
          }
        }

        if (!city) continue;
        if (seen.has(city.toLowerCase())) continue;
        seen.add(city.toLowerCase());
        // query = "City,CC" fallback; lat/lng (when known) are used for the weather lookup
        const query = countryCode ? `${city},${countryCode}` : city;
        result.push({ label: city, query, lat, lng });
      }

      setCities(result);
    };

    resolve();
  }, [segments, tripId]);

  if (cities.length === 0) return null;

  return (
    <>
      <div className="weather-strip" ref={stripRef} style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 16px", borderBottom: "1px solid var(--border)",
        overflowX: "auto", overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        background: "var(--paper)",
        flexWrap: "nowrap",
      }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>🌤</span>
        {cities.map((city, i) => (
          <button
            key={i}
            onClick={() => setSelectedCity(city)}
            style={{
              background: "none", border: "1px solid var(--border)",
              borderRadius: 20, padding: "4px 12px",
              fontSize: 13, color: "var(--ink-mid)", cursor: "pointer",
              whiteSpace: "nowrap", flexShrink: 0,
              fontFamily: "var(--font-body)",
            }}
          >
            {city.label}
          </button>
        ))}
      </div>

      {selectedCity && (
        <WeatherModal
          cityName={selectedCity.query}
          displayName={selectedCity.label}
          lat={selectedCity.lat}
          lng={selectedCity.lng}
          onClose={() => setSelectedCity(null)}
        />
      )}
    </>
  );
}
